/**
 * The client's side of the REST API.
 *
 * **Every call is optional.** The whole thing is wrapped so that an unreachable API is a
 * quiet absence rather than an error: solo racing, the bot, Kid Mode and every setting
 * work with no account at all, and always will. The account adds coins, a locker and a
 * history on top of a game that is complete without them.
 *
 * That is not defensive coding, it is the offline-first requirement in docs/02 §2.2 —
 * "nothing blocks on the network" — applied consistently. A player on a train should get
 * a race, not a spinner.
 */

import type { ChallengeRun, GhostRun } from '@dash-duel/protocol';
import { DEFAULT_API_PORT, httpOrigin, resolveEndpoint } from './endpoints.js';

export const API_URL = resolveEndpoint(
  import.meta.env.VITE_API_URL,
  httpOrigin(location, DEFAULT_API_PORT),
);
const TOKEN_KEY = 'dashduel.token';
const DEVICE_KEY = 'dashduel.device';

export interface PublicPlayer {
  id: string;
  displayName: string;
  isGuest: boolean;
}

export interface StoreItem {
  id: string;
  kind: string;
  displayName: string;
  priceCoins: number | null;
  priceCents: number | null;
  owned: boolean;
  equipped: boolean;
}

export interface Me {
  player: PublicPlayer;
  balance: number;
  loadout: Record<string, string>;
  stats: {
    racesPlayed: number;
    racesWon: number;
    bestScore: number;
    totalCoins: number;
  };
  /**
   * The daily challenge, as of this load.
   *
   * Optional so a client talking to an older API still works rather than rendering
   * "undefined" on the title screen — the card simply does not appear.
   */
  daily?: { streak: number; playedToday: boolean };
  /** Achievement ids held, oldest first. Absent from an older API. */
  achievements?: string[];
}

/**
 * A stable per-install identity.
 *
 * Not a fingerprint and not tied to anything about the person — a random value, generated
 * once and kept in local storage. It is a bearer credential, which is exactly why it never
 * leaves this device except to be exchanged for a token.
 */
function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export class ApiClient {
  private token: string | null = localStorage.getItem(TOKEN_KEY);
  private me: Me | null = null;

  /** The token the match server needs, or null if we never got one. */
  get sessionToken(): string | null {
    return this.token;
  }

  get profile(): Me | null {
    return this.me;
  }

  get online(): boolean {
    return this.me !== null;
  }

  /**
   * At page load: pick up the account this browser already has, and create nothing.
   *
   * Creating a guest here used to mean an account for every browser that ever *opened*
   * the page — search-engine renderers, link previews, people who left after a second.
   * Production had about 130 accounts, a handful of which had ever run a race; Googlebot
   * alone made one per render, each named the same because its renderer makes
   * `Math.random` repeat. An account now appears the first time one is needed.
   */
  async resume(): Promise<boolean> {
    if (!this.token) return false;
    try {
      return await this.refresh();
    } catch {
      return false;
    }
  }

  /**
   * Make sure there is an account, creating a guest if this browser has none — called
   * when something is about to need one: a finished race to record, a duel to credit, a
   * device to link. Safe to fail; returns whether there is an account afterwards.
   */
  async ensureAccount(displayName: string): Promise<boolean> {
    if (this.me) return true;
    try {
      if (this.token && (await this.refresh())) return true;
      if (!this.token) {
        const auth = await this.post<{ token: string }>('/auth/guest', {
          deviceId: deviceId(),
          displayName,
        });
        if (!auth) return false;
        this.token = auth.token;
        localStorage.setItem(TOKEN_KEY, auth.token);
      }
      return await this.refresh();
    } catch {
      return false;
    }
  }

  async refresh(): Promise<boolean> {
    const { body, rejected } = await this.get<Me>('/me', true);
    if (!body) {
      // **Only on an actual rejection.** A token the server will not accept is worse than
      // none — it would be sent to the match server and refused there too — but this used
      // to drop the token for *any* failure, including being offline for a moment.
      //
      // That was survivable while a device id always found its account back: the next
      // launch signed in again and nothing was visibly lost. It stopped being survivable
      // when accounts became linkable, because a linked device's coins live on an account
      // its device id no longer created — so one bad request could hand it a new empty
      // account and strand the old one. This is the offline-first rule in docs/02 §2.2
      // applied to the one thing that was ignoring it.
      if (rejected && this.token) {
        localStorage.removeItem(TOKEN_KEY);
        this.token = null;
        // The account is gone, so is what was known about it. A failure that is only the
        // network keeps it: the coins did not stop existing because a phone woke up offline.
        this.me = null;
      }
      return false;
    }
    this.me = body;
    return true;
  }

  /**
   * Report a finished solo race, so it pays.
   *
   * The server replays the log and pays on *its* number, so everything here is a claim
   * being offered for checking rather than a result being asserted. Sending the seed and
   * the three track flags is not optional detail: they are what the replay regenerates
   * the world from, and a lone run and a run against a bot produce different tracks from
   * the same seed.
   *
   * Best-effort. A failure here costs the player their coins for one race, which is worth
   * far less than a scoreboard that will not appear because a fetch rejected — so this
   * never throws and never blocks the results screen.
   */
  async reportSolo(run: SoloRun): Promise<SoloOutcome | null> {
    if (!this.token) return null;
    const response = await this.postRaw('/races/solo', run);
    if (!response?.ok) return null;
    const body = (await response.json().catch(() => null)) as SoloResponse | null;
    if (!body) return null;
    // The balance on screen is now stale whether or not this one paid. Refreshing also
    // brings back the new streak, which the title card reads on the way out.
    await this.refresh();
    return {
      reward: body.reward,
      daily: body.daily,
      raceId: body.raceId ?? null,
      validation: body.validation,
      achievements: body.achievements ?? [],
    };
  }

  /**
   * A challenge link for a race already reported. The same race always gets the same code.
   *
   * @returns the code, or why not: `offline` for anything that looks like the network.
   */
  async createChallenge(raceId: string): Promise<{ code: string } | { error: string }> {
    const response = await this.postRaw('/challenges', { raceId });
    if (!response) return { error: 'offline' };
    const body = (await response.json().catch(() => null)) as { code?: string; error?: string } | null;
    if (response.ok && body?.code) return { code: body.code };
    return { error: body?.error ?? 'offline' };
  }

  /** A challenge by its code. Public — the friend opening it may have no account yet. */
  async challenge(code: string): Promise<ChallengeRun | 'missing' | null> {
    try {
      const response = await fetch(`${API_URL}/challenges/${encodeURIComponent(code)}`);
      if (response.status === 404) return 'missing';
      if (!response.ok) return null;
      return (await response.json()) as ChallengeRun;
    } catch {
      return null;
    }
  }

  /**
   * "Report a bug or suggest an idea". Works without an account. Returns whether it
   * arrived; `slow` when the server asked for a pause between messages.
   */
  async sendFeedback(feedback: { kind: 'bug' | 'idea'; message: string; build: string; context: Record<string, string | number | boolean | null> }): Promise<'ok' | 'slow' | 'failed'> {
    const response = await this.postRaw('/feedback', feedback);
    if (response?.ok) return 'ok';
    return response?.status === 429 ? 'slow' : 'failed';
  }

  /** Send a tester's problem report. Returns its id, or null if it did not arrive. */
  async report(report: TesterReport): Promise<string | null> {
    const body = await this.post<{ id: string }>('/reports', report).catch(() => null);
    return body?.id ?? null;
  }

  /** Your record against one person; null if never raced, or offline. */
  async rivalry(playerId: string): Promise<Rivalry | null> {
    const { body } = await this.get<{ rivalry: Rivalry | null }>(`/rivals/${encodeURIComponent(playerId)}`);
    return body?.rivalry ?? null;
  }

  /** Everyone you have raced, most-raced first. Null when offline. */
  async rivals(): Promise<Rivalry[] | null> {
    const { body } = await this.get<{ rivals: Rivalry[] }>('/me/rivals');
    return body?.rivals ?? null;
  }

  /** Today's challenge, ranked. Null when offline. */
  async dailyBoard(): Promise<DailyBoard | null> {
    const { body } = await this.get<DailyBoard>('/daily/board');
    return body;
  }

  /** One leaderboard. Null when offline, or when the server has no Redis to rank with. */
  async leaderboard(kind: 'weekly' | 'global', duration: number): Promise<Leaderboard | null> {
    const { body } = await this.get<Leaderboard>(`/leaderboard/${kind}?duration=${duration}&limit=50`);
    return body;
  }

  /**
   * A real recorded run near this player's level, for an empty quick-match queue.
   * Null when there is none (the server answers 204) or the network failed.
   */
  async pickGhost(kidMode: boolean): Promise<GhostRun | null> {
    if (!this.token) return null;
    try {
      const response = await fetch(`${API_URL}/ghosts/pick?kidMode=${kidMode ? 1 : 0}`, {
        headers: this.headers(),
      });
      if (response.status !== 200) return null;
      return (await response.json()) as GhostRun;
    } catch {
      return null;
    }
  }

  /**
   * Ask for a code that lets another device adopt this account.
   *
   * Shown on the device you want to *keep*. The account with the history is the one that
   * survives a link, so this is the end that has to start it.
   */
  async linkCode(): Promise<{ display: string; expiresAt: string } | null> {
    const response = await this.postRaw('/link/code', {});
    if (!response?.ok) return null;
    return (await response.json().catch(() => null)) as {
      display: string;
      expiresAt: string;
    } | null;
  }

  /**
   * Spend a code: adopt the account it names, bringing this device's coins along.
   *
   * The new token is stored **before** anything else, because the old one names a player
   * that no longer exists — a failure after this point leaves the device signed into the
   * right account with a stale profile, which the next refresh fixes. The other order
   * leaves it signed into nothing.
   */
  async claimLink(code: string): Promise<{ ok: true; coinsMerged: number } | { ok: false; error: string }> {
    const response = await this.postRaw('/link/claim', { code });
    if (!response) return { ok: false, error: 'offline' };

    const body = (await response.json().catch(() => ({}))) as {
      token?: string;
      coinsMerged?: number;
      error?: string;
    };
    if (!response.ok || !body.token) return { ok: false, error: body.error ?? 'failed' };

    this.token = body.token;
    localStorage.setItem(TOKEN_KEY, body.token);
    await this.refresh();
    return { ok: true, coinsMerged: body.coinsMerged ?? 0 };
  }

  /** This player's latest races, newest first, or null if the API cannot be reached. */
  async history(limit = 20): Promise<RaceHistory[] | null> {
    return (await this.get<{ races: RaceHistory[] }>(`/me/history?limit=${limit}`)).body?.races ?? null;
  }

  async store(): Promise<{ items: StoreItem[]; balance: number } | null> {
    return (await this.get<{ items: StoreItem[]; balance: number }>('/store')).body;
  }

  async purchase(itemId: string): Promise<{ ok: boolean; error?: string }> {
    const result = await this.postRaw('/store/purchase', { itemId });
    if (!result) return { ok: false, error: 'offline' };
    if (result.ok) {
      await this.refresh();
      return { ok: true };
    }
    const body = (await result.json().catch(() => ({}))) as { error?: string };
    return { ok: false, error: body.error ?? 'failed' };
  }

  async equip(itemId: string): Promise<boolean> {
    const result = await this.postRaw('/store/equip', { itemId });
    if (result?.ok) await this.refresh();
    return Boolean(result?.ok);
  }

  /**
   * Finish a Google sign-in: trade the ID token for this game's own token.
   *
   * Sends this browser's current token too, if it has one, so the server can upgrade the
   * guest here (first sign-in) or fold it into the Google account that already exists
   * (every browser after) — see `/auth/google`.
   */
  async signInWithGoogle(
    idToken: string,
    nonce: string,
    displayName: string,
  ): Promise<{ ok: true; coinsMerged: number } | { ok: false; error: string }> {
    let response: Response;
    try {
      response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify({ idToken, nonce, displayName }),
      });
    } catch {
      return { ok: false, error: 'offline' };
    }
    const body = (await response.json().catch(() => ({}))) as {
      token?: string;
      coinsMerged?: number;
      error?: string;
    };
    if (!response.ok || !body.token) return { ok: false, error: body.error ?? 'failed' };

    this.token = body.token;
    localStorage.setItem(TOKEN_KEY, body.token);
    await this.refresh();
    return { ok: true, coinsMerged: body.coinsMerged ?? 0 };
  }

  /** Whether this browser is signed in with Google rather than playing as a guest. */
  get signedIn(): boolean {
    return this.me !== null && !this.me.player.isGuest;
  }

  /**
   * Sign this browser out. The account itself is untouched on the server — signing in
   * again, here or anywhere, gets it back.
   *
   * The device id goes too. Left behind, the next race would ask `/auth/guest` with it,
   * which still points at the signed-in account — so "signed out" would quietly be signed
   * straight back in, which on a shared computer is the whole thing sign-out exists to stop.
   */
  signOut(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(DEVICE_KEY);
    this.token = null;
    this.me = null;
  }

  /**
   * Delete this account, permanently.
   *
   * The local token and device id go too, and they go *whatever the server said*. Leaving
   * them behind would sign the player straight back into an account that no longer exists
   * on the next launch — and if the request did reach the server, the device id is the one
   * remaining thing on this machine that pointed at the deleted player.
   *
   * A fresh guest is created on the next visit, which is the honest outcome: they asked to
   * be forgotten, not to be locked out of the game.
   */
  async deleteAccount(): Promise<boolean> {
    let ok = false;
    try {
      // Auth only, deliberately. `headers()` sets `content-type: application/json`, and
      // Fastify refuses a request that declares a JSON body and then does not send one —
      // this returned 400 until a real request was watched going out.
      const response = await fetch(`${API_URL}/me`, {
        method: 'DELETE',
        headers: this.authOnly(),
      });
      ok = response.ok;
    } catch {
      ok = false;
    }
    if (ok) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(DEVICE_KEY);
      this.token = null;
      this.me = null;
    }
    return ok;
  }

  // ── Plumbing ───────────────────────────────────────────────────────────────

  /**
   * Just the bearer token, for requests that carry no body.
   *
   * Separate from `headers()` because declaring a JSON content type without a body is a
   * 400 from Fastify, not a harmless extra header.
   */
  private authOnly(): Record<string, string> {
    return this.token ? { authorization: `Bearer ${this.token}` } : {};
  }

  private headers(): Record<string, string> {
    return {
      'content-type': 'application/json',
      ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
    };
  }

  /**
   * @param missingMeansRejected treat a 404 as a dead token too. True only for `/me`,
   *   where "no such player" means the account this token names is gone — deleted, or
   *   wiped server-side — and holding on to it would leave the device signed in to
   *   nothing forever, since every other failure is (rightly) assumed to be the network.
   * @returns the body, or null for any failure. `rejected` distinguishes the one failure
   *   that means the token is bad from every failure that means the network is.
   */
  private async get<T>(
    path: string,
    missingMeansRejected = false,
  ): Promise<{ body: T | null; rejected: boolean }> {
    try {
      const response = await fetch(`${API_URL}${path}`, { headers: this.headers() });
      if (!response.ok) {
        const gone = missingMeansRejected && response.status === 404;
        return { body: null, rejected: response.status === 401 || response.status === 403 || gone };
      }
      return { body: (await response.json()) as T, rejected: false };
    } catch {
      // Offline, DNS, a dead server. The token is very probably fine.
      return { body: null, rejected: false };
    }
  }

  private async post<T>(path: string, body: unknown): Promise<T | null> {
    const response = await this.postRaw(path, body);
    if (!response?.ok) return null;
    return (await response.json()) as T;
  }

  private async postRaw(path: string, body: unknown): Promise<Response | null> {
    try {
      return await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(body),
      });
    } catch {
      return null;
    }
  }
}

/** One finished race, as the player's own history returns it. */
export interface RaceHistory {
  raceId: string;
  /** `solo`, `daily`, `private`, `quick`, `ranked` or `ghost`. */
  mode: string;
  endedAt: string;
  durationMs?: number;
  kidMode?: boolean;
  score: number;
  distance?: number;
  coins: number;
  nearMisses?: number;
  crashes?: number;
  powerUps?: number | null;
  /** How long the player lasted; below the race length means they crashed out. */
  timeSurvivedMs?: number | null;
  /** The bot a solo race was against (`easy`/`normal`/`hard`), or null. */
  botSkill?: string | null;
  isWinner: boolean;
  /** Set for a race against another person; null for solo, daily and bot races. */
  opponentName: string | null;
  opponentScore: number | null;
}

/** Everything the server needs to regenerate the track and replay the run on it. */
export interface SoloRun {
  seed: number;
  duration: number;
  kidMode: boolean;
  slipstream: boolean;
  /**
   * Which bot was raced, or null for a run against the clock alone.
   *
   * Not a boolean: the server re-runs this exact opponent to derive the freezes it landed
   * and the scores it was posting, because none of that is in the input log. It also
   * decides the track, since a race with nobody to freeze swaps that pickup for a Shield.
   */
  botSkillId: string | null;
  inputLog: number[];
  claimedScore: number;
  claimedEndTick: number;
  /** Whether this was today's challenge. The server checks the seed rather than trusting it. */
  daily?: boolean;
  /** This run's id, the same on any retry — so a replayed daily is recorded, a resend is not. */
  runId?: string;
  /** The challenge link this run raced, so it counts in the two players' record. */
  challengeCode?: string;
}

export interface SoloReward {
  coins: number;
  completion: number;
  win: number;
  firstWinOfDay: number;
  total: number;
}

interface SoloResponse {
  recorded: boolean;
  /** Achievements this run earned for the first time. Absent from an older API. */
  achievements?: string[];
  /** Absent from an API one deploy behind. */
  raceId?: string;
  validation: 'verified' | 'mismatch';
  score: number;
  /** Null on a replayed submission, which paid nothing because it had already paid. */
  reward: SoloReward | null;
  /** Present only for a challenge run. `bonus` is 0 when today was already claimed. */
  daily: { streak: number; bonus: number } | null;
}

export interface SoloOutcome {
  /** Achievements this run earned for the first time. */
  achievements: string[];
  /** The recorded race, which is what a challenge link is made from. */
  raceId: string | null;
  validation: 'verified' | 'mismatch';
  reward: SoloReward | null;
  daily: { streak: number; bonus: number } | null;
}

export interface LeaderboardEntry {
  rank: number;
  playerId: string;
  displayName: string;
  score: number;
}

export interface DailyBoard {
  day: string;
  duration: number;
  /** How many people have run today's track. */
  players: number;
  entries: LeaderboardEntry[];
  you: { rank: number; score: number } | null;
  resetsAt: string;
}

export interface Leaderboard {
  kind: 'weekly' | 'global' | 'friends' | 'daily';
  /** Only for today's board: how many people have run the track. */
  players?: number;
  /** Race length in seconds: one board per length. */
  duration: number;
  seasonNumber: number;
  /** ISO time the board starts over. */
  resetsAt: string;
  entries: LeaderboardEntry[];
  /** This player's place, even outside the top 50; null if they have no run on it. */
  you: { rank: number; score: number } | null;
}

export type RivalOutcome = 'win' | 'loss' | 'draw';

/** You against one person — see apps/api/src/races/rivals.ts. */
export interface Rivalry {
  opponent: { id: string; name: string };
  wins: number;
  losses: number;
  draws: number;
  streak: { holder: 'you' | 'them'; count: number } | null;
  /** Newest first. */
  recent: RivalOutcome[];
}

/** Tester mode's report: the track, the moments marked, a note and the run's inputs. */
export interface TesterReport {
  mode: string;
  seed: number;
  duration: number;
  kidMode: boolean;
  hasOpponent: boolean;
  trackVersion: number;
  score: number;
  marks: { time: number; piece: string }[];
  note: string;
  inputLog: number[];
}
